/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiDescriptionListTitle } from './description_list_title';
import { EuiDescriptionListDescription } from './description_list_description';
import { useEuiTheme } from '../../services';
import { euiDescriptionListStyles } from './description_list.styles';
import { EuiDescriptionListContext } from './description_list_context';
export var EuiDescriptionList = ({
  align = 'left',
  children,
  className,
  compressed = false,
  descriptionProps,
  listItems,
  textStyle = 'normal',
  titleProps,
  type = 'row',
  gutterSize = 'm',
  ...rest
}) => {
  const euiTheme = useEuiTheme();
  const styles = euiDescriptionListStyles(euiTheme);
  const cssStyles = [styles.euiDescriptionList, styles[type], styles[align]];
  const classes = classNames('euiDescriptionList', className);
  let childrenOrListItems = null;
  if (listItems) {
    childrenOrListItems = listItems.map((item, index) => {
      return [<EuiDescriptionListTitle key={`title-${index}`} {...titleProps}>
          {item.title}
        </EuiDescriptionListTitle>, <EuiDescriptionListDescription key={`description-${index}`} {...descriptionProps}>
          {item.description}
        </EuiDescriptionListDescription>];
    });
  } else {
    childrenOrListItems = children;
  }
  return <EuiDescriptionListContext.Provider value={{
    type,
    compressed,
    textStyle,
    align,
    gutterSize
  }}>
      <dl className={classes} css={cssStyles} {...rest} data-type={type}>
        {childrenOrListItems}
      </dl>
    </EuiDescriptionListContext.Provider>;
};
EuiDescriptionList.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  listItems: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.any.isRequired,
    description: PropTypes.any.isRequired
  }).isRequired),
  /**
     * Text alignment
     */
  align: PropTypes.any,
  /**
     * Smaller text and condensed spacing
     */
  compressed: PropTypes.bool,
  /**
     * How should the content be styled, by default
     * this will emphasize the title
     */
  textStyle: PropTypes.oneOf(["normal", "reverse"]),
  /**
     * How each item should be laid out
     */
  type: PropTypes.any,
  /**
     * Props object to be passed to `EuiDescriptionListTitle`
     */
  titleProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }),
  /**
     * Props object to be passed to `EuiDescriptionListDescription`
     */
  descriptionProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }),
  /**
     * Vertical spacing added between `EuiDescriptionList` elements
     */
  gutterSize: PropTypes.any
};
try {
  EuiDescriptionList.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/description_list/description_list.tsx',
    description: '',
    displayName: 'EuiDescriptionList',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      listItems: {
        defaultValue: null,
        description: '',
        name: 'listItems',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: '{ title: ReactNode; description: ReactNode; }[]'
        }
      },
      align: {
        defaultValue: {
          value: 'left'
        },
        description: 'Text alignment',
        name: 'align',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"left" | "center"',
          value: [{
            value: '"left"'
          }, {
            value: '"center"'
          }]
        }
      },
      compressed: {
        defaultValue: {
          value: 'false'
        },
        description: 'Smaller text and condensed spacing',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      textStyle: {
        defaultValue: {
          value: 'normal'
        },
        description: 'How should the content be styled, by default\n' + 'this will emphasize the title',
        name: 'textStyle',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"normal" | "reverse"',
          value: [{
            value: '"normal"'
          }, {
            value: '"reverse"'
          }]
        }
      },
      type: {
        defaultValue: {
          value: 'row'
        },
        description: 'How each item should be laid out',
        name: 'type',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"row" | "inline" | "column" | "responsiveColumn"',
          value: [{
            value: '"row"'
          }, {
            value: '"inline"'
          }, {
            value: '"column"'
          }, {
            value: '"responsiveColumn"'
          }]
        }
      },
      titleProps: {
        defaultValue: null,
        description: 'Props object to be passed to `EuiDescriptionListTitle`',
        name: 'titleProps',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLElement> & CommonProps'
        }
      },
      descriptionProps: {
        defaultValue: null,
        description: 'Props object to be passed to `EuiDescriptionListDescription`',
        name: 'descriptionProps',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLElement> & CommonProps'
        }
      },
      gutterSize: {
        defaultValue: {
          value: 'm'
        },
        description: 'Vertical spacing added between `EuiDescriptionList` elements',
        name: 'gutterSize',
        parent: {
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        },
        declarations: [{
          fileName: 'app/src/components/description_list/description_list_types.ts',
          name: 'EuiDescriptionListProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiDescriptionListProps']
  };
} catch (e) {}